import { useState } from 'react';
import { Square } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import {
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { PROJECT_PALETTE, fmtDec, fmtHMS, hours, relativeTime } from '@foodstyles/shared';
import { useNow } from '../../context/NowContext';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { useForceStopEntry } from '../../hooks/useEntries';
import { ApiError, apiGet, qs } from '../../lib/api';
import { qk } from '../../lib/queryKeys';
import { Avatar, Card, EmptyState, RoleBadge, SectionTitle, Spinner } from '../../components/primitives';
import { RangePicker, useDateRange } from '../../components/RangePicker';
import type { ActivityResponse, DashboardResponse } from '../../types/api';

const color = (i: number) => PROJECT_PALETTE[i % PROJECT_PALETTE.length];

export function DashboardScreen() {
  const range = useDateRange('week');
  const [onlyMe, setOnlyMe] = useState(false);
  const { from, to } = range.range;
  const { permissions } = useAuth();
  const toast = useToast();
  const forceStop = useForceStopEntry();

  const dash = useQuery({
    queryKey: qk.dashboard(from, to, onlyMe),
    queryFn: () => apiGet<DashboardResponse>(`/api/dashboard${qs({ from, to, onlyMe })}`),
  });
  const activity = useQuery({
    queryKey: qk.activity,
    queryFn: () => apiGet<ActivityResponse>('/api/activity'),
    refetchInterval: 60_000,
    enabled: !onlyMe,
  });

  const rows = dash.data?.rows ?? [];
  const totalMs = dash.data?.totalMs ?? 0;
  const chartData = rows.map((r) => ({ name: r.name, value: hours(r.ms), colorIndex: r.colorIndex }));

  // Admin hard-stop: someone forgot to stop their clock.
  const onForceStop = (p: ActivityResponse['people'][number]) => {
    if (!p.running) return;
    const elapsed = fmtHMS(Date.now() - p.running.startMs);
    if (!confirm(`Stop ${p.name}'s timer?\n\nIt has been running for ${elapsed}. The entry is kept and they can edit its details afterwards.`)) return;
    forceStop.mutate(p.running.entryId, {
      onSuccess: () => toast(`${p.name}'s timer stopped.`, 'success'),
      onError: (e) => toast(e instanceof ApiError ? e.message : 'Could not stop the timer.'),
    });
  };

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <RangePicker state={range} exclude={[]} />
        <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-slate-600 dark:text-slate-300">
          <span className="relative inline-flex">
            <input type="checkbox" className="peer sr-only" checked={onlyMe} onChange={(e) => setOnlyMe(e.target.checked)} />
            <span className="h-5 w-9 rounded-full bg-slate-200 transition-colors peer-checked:bg-[#FF5A3C] dark:bg-slate-700" />
            <span className="absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white transition-transform peer-checked:translate-x-4 dark:bg-slate-200" />
          </span>
          Only me
        </label>
      </div>

      <div className={onlyMe ? 'space-y-5' : 'grid grid-cols-1 gap-5 lg:grid-cols-3'}>
        <div className={onlyMe ? 'space-y-5' : 'space-y-5 lg:col-span-2'}>
          <Card>
            <SectionTitle sub="Hours per project in the selected range">Time breakdown</SectionTitle>
            {dash.isLoading ? (
              <div className="flex justify-center py-12">
                <Spinner className="h-7 w-7" />
              </div>
            ) : rows.length === 0 ? (
              <EmptyState>No tracked time in this range.</EmptyState>
            ) : (
              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="relative">
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie data={chartData} dataKey="value" innerRadius={58} outerRadius={92} paddingAngle={2}>
                        {chartData.map((d, i) => (
                          <Cell key={i} fill={color(d.colorIndex)} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v: number) => `${v.toFixed(2)} h`} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                    <span className="font-mono text-2xl font-bold text-slate-800 dark:text-slate-100">{fmtDec(totalMs)}</span>
                    <span className="text-xs text-slate-400">hours</span>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={chartData} layout="vertical" margin={{ left: 8, right: 12 }}>
                    <XAxis type="number" tick={{ fontSize: 11, fill: '#64748b' }} />
                    <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11, fill: '#64748b' }} />
                    <Tooltip formatter={(v: number) => `${v.toFixed(2)} h`} cursor={{ fill: 'rgba(148,163,184,0.15)' }} />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                      {chartData.map((d, i) => (
                        <Cell key={i} fill={color(d.colorIndex)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>

          <Card pad={false}>
            <div className="px-5 py-4">
              <SectionTitle>Totals by project</SectionTitle>
            </div>
            <table className="w-full text-sm">
              <tbody>
                {rows.map((r) => (
                  <tr key={r.projectId ?? 'none'} className="border-t border-slate-100 dark:border-slate-700">
                    <td className="px-5 py-2.5">
                      <span className="mr-2 inline-block h-3 w-3 rounded-sm align-middle" style={{ background: color(r.colorIndex) }} />
                      {r.name}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono tabular-nums text-slate-700 dark:text-slate-200">{fmtDec(r.ms)}</td>
                    <td className="w-16 px-5 py-2.5 text-right text-slate-400">{r.pct}%</td>
                  </tr>
                ))}
                {rows.length > 0 && (
                  <tr className="border-t border-slate-200 font-semibold dark:border-slate-700">
                    <td className="px-5 py-2.5">Total</td>
                    <td className="px-3 py-2.5 text-right font-mono tabular-nums text-slate-800 dark:text-slate-100">{fmtDec(totalMs)}</td>
                    <td className="px-5 py-2.5" />
                  </tr>
                )}
              </tbody>
            </table>
          </Card>
        </div>

        {!onlyMe && (
          <Card pad={false} className="lg:col-span-1">
            <div className="flex items-center justify-between px-5 py-4">
              <SectionTitle>Team activeness</SectionTitle>
              <span className="text-[11px] text-slate-400">Live — refreshes ~60s</span>
            </div>
            <Activeness
              people={activity.data?.people ?? []}
              loading={activity.isLoading}
              onForceStop={permissions?.manageUsers ? onForceStop : undefined}
            />
          </Card>
        )}
      </div>
    </div>
  );
}

function Activeness({
  people,
  loading,
  onForceStop,
}: {
  people: ActivityResponse['people'];
  loading: boolean;
  onForceStop?: (p: ActivityResponse['people'][number]) => void;
}) {
  const now = useNow();
  if (loading)
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  if (people.length === 0) return <EmptyState>No teammates to show.</EmptyState>;
  return (
    <div className="max-h-[560px] divide-y divide-slate-100 overflow-y-auto dark:divide-slate-700">
      {people.map((p) => (
        <div key={p.id} className="flex items-center gap-3 px-5 py-3">
          <Avatar name={p.name} size={34} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="truncate text-sm font-semibold text-slate-800 dark:text-slate-100">{p.name}</span>
              <RoleBadge role={p.role} sm />
            </div>
            {p.running ? (
              <div className="truncate text-xs text-slate-500 dark:text-slate-400">
                {[p.running.projectName, p.running.taskName].filter(Boolean).join(' · ') || 'Tracking'}
              </div>
            ) : (
              <div className="text-xs text-slate-400">
                {p.lastStopMs ? `Last clocked: ${relativeTime(p.lastStopMs, now)}` : 'No activity yet'}
              </div>
            )}
          </div>
          {p.running ? (
            <div className="flex items-center gap-2">
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
              </span>
              <span className="font-mono text-xs font-semibold tabular-nums text-emerald-600 dark:text-emerald-400">{fmtHMS(now - p.running.startMs)}</span>
              {onForceStop && (
                <button
                  onClick={() => onForceStop(p)}
                  title="Stop this timer (Admin)"
                  aria-label={`Stop ${p.name}'s timer`}
                  className="rounded p-1 text-rose-500 hover:bg-rose-50 hover:text-rose-600 dark:text-rose-400 dark:hover:bg-rose-500/10 dark:hover:text-rose-400"
                >
                  <Square size={13} fill="currentColor" />
                </button>
              )}
            </div>
          ) : (
            <span className="h-2.5 w-2.5 rounded-full bg-slate-300 dark:bg-slate-600" />
          )}
        </div>
      ))}
    </div>
  );
}
